import { Text, Box, Heading, Icon, Divider } from '@medely/ui-kit';
import React from 'react';
import { useGate } from '@mp/hooks';
import { WhatToExpectInfo } from '../whatToExpectModal/WhatToExpectInfo';
import { CancelationPolicy } from '../jobs/CancelationPolicy';
import { JobDetailsListItem } from '../jobs/JobDetailsListItem';
import { EstimatedPayDisclaimer, ShiftDate, ShiftLocation } from '../jobs';
import DisplayHelpers from '../../utils/displayHelpers';
import { PostBookingInfoLegacyProps } from './interface';
import { gateNames } from '../../constants';
import { HideFrom } from '../HideFrom';
import { parseFragmentData } from '@mp/graphql/generated';
import { JobDetailsFragment } from '../jobs/jobDetailsComponents/graphql';
import { ExtendedRateInfo } from '../shared/ExtendedRateInfo';

export const PostBookingInfoLegacy = ({
  jobMap,
  job: inputJob,
  cancelationPolicyButton,
  userCoordinates,
  smallIcon = false,
}: PostBookingInfoLegacyProps) => {
  const job = parseFragmentData(JobDetailsFragment, inputJob);
  const showXRInfo = useGate(gateNames.xrTransparency);

  return (
    <Box px={1}>
      <Text size="l" color="text.primary">
        You are now scheduled to work a shift at {job?.location?.name}.
      </Text>
      <Box mt={2}>{jobMap}</Box>
      <Box py={2}>
        <Box py={1}>
          <JobDetailsListItem
            icon={
              <Icon
                name="light-location-dot"
                iconSize={smallIcon ? '12px' : '18px'}
                color="text.primary"
                variant="custom"
              />
            }
            title="Location"
          >
            <ShiftLocation
              bookVariant="post-book"
              distanceVariant="local"
              job={inputJob}
              userCoordinates={userCoordinates}
            />
          </JobDetailsListItem>
        </Box>
        <Box py={1}>
          <JobDetailsListItem
            icon={
              <Icon
                name="light-calendar"
                iconSize={smallIcon ? '12px' : '18px'}
                color="text.primary"
                variant="custom"
              />
            }
            title="Date"
          >
            <ShiftDate job={inputJob} />
          </JobDetailsListItem>
        </Box>
        <Box py={1}>
          <JobDetailsListItem
            icon={
              <Icon
                name="light-clock"
                iconSize={smallIcon ? '12px' : '18px'}
                color="text.primary"
                variant="custom"
              />
            }
            title="Shift time"
          >
            <Text size="m">
              {DisplayHelpers.job.time({
                current_starts_time: job?.current_starts_time,
                current_ends_time: job?.current_ends_time,
                approved_clock_in: job?.approved_clock_in,
                approved_clock_out: job?.approved_clock_out,
                guaranteed_minimum_hours: job?.guaranteed_minimum_hours,
                status: job?.status,
                shift_type: job?.shift_type,
                assignment: job?.assignment,
                location: job?.location,
                options: { scheduledShiftStyle: true },
              })}
            </Text>
          </JobDetailsListItem>
        </Box>
        <HideFrom saas agency>
          {showXRInfo && (
            <Box py={1}>
              <ExtendedRateInfo
                job={inputJob}
                smallIcon={smallIcon}
                iconSize={smallIcon ? '12px' : '18px'}
              />
            </Box>
          )}
        </HideFrom>
        <HideFrom saas agency>
          <Box py={1}>
            <JobDetailsListItem
              icon={
                <Icon
                  name="light-info-circle"
                  iconSize={smallIcon ? '12px' : '18px'}
                  color="text.primary"
                  variant="custom"
                />
              }
            >
              <EstimatedPayDisclaimer />
            </JobDetailsListItem>
          </Box>
        </HideFrom>
      </Box>
      <Divider />
      <Box py={2}>
        <HideFrom saas>
          <CancelationPolicy />
        </HideFrom>
        <Box width="45%">{cancelationPolicyButton}</Box>
      </Box>
      <HideFrom saas>
        <Divider />
        <Box py={2}>
          <Heading size="xs" color="text.primary">
            What to expect
          </Heading>
        </Box>
        <WhatToExpectInfo />
      </HideFrom>
    </Box>
  );
};
